<template>
  <div
    v-bind="$data"
    class="e-dropdown"
    :class="[{ disabled, 'has-error': errorMsg, [`theme-${theme}`]: theme }]"
    tabindex="-1"
    ref="rootEl"
  >
    <div class="e-dropdown__title" v-html="title" />
    <div
      class="e-dropdown__label"
      :label="`${label}${required ? '*' : ''}`"
      v-html="`${label}${required ? '*' : ''}`"
      v-if="label"
    />
    <div class="e-dropdown__main" :class="[{ active: deviceType.includes('desktop') && active }]">
      <div class="e-dropdown__selection" @click="toggle">
        <div class="e-dropdown__text" v-if="selectedOption.length > 0" title="selectedOption.text">
          {{ selectedOptionText }}
        </div>
        <div class="e-dropdown__placeholder" v-else>{{ placeholder }}</div>
        <icon
          class="e-dropdown__clear"
          name="close"
          @click="clear"
          v-if="selectedOption.length > 0"
        />
        <icon class="e-dropdown__arrow" name="down" v-else />
      </div>
      <transition name="options-ani">
        <div class="e-dropdown__options-wrapper" v-if="deviceType.includes('desktop') && active">
          <div class="e-dropdown__search" v-if="showSearch">
            <div class="e-dropdown__search-control">
              <icon name="search" />
              <input type="text" autocomplete="newPassword" v-model="keyword" ref="searchEl" />
            </div>
          </div>
          <div class="e-dropdown__options">
            <div
              class="e-dropdown__option placeholder"
              @click="clearSelection"
              v-if="clearable && !$isNoE(selectedOption?.code)"
            >
              {{ $ifEmpty(clearText, placeholder) }}
            </div>
            <div
              class="e-dropdown__option"
              :class="{ selected: selectedOptionIds.includes(option?.code) }"
              v-for="(option, index) in filteredOptions"
              :key="index"
              @click="(e) => onOptionClick(e, option)"
            >
              <slot name="option" v-if="$slots.option" :option="option" />
              <div v-else>{{ option.text }}</div>
            </div>
          </div>
        </div>
      </transition>
    </div>
    <div class="e-dropdown__desc" v-if="!$isNoE(description)" v-html="description" />
    <div class="e-dropdown__error-box">
      <div class="e-dropdown__error-msg" v-if="errorMsg">
        <span class="e-dropdown__msg">{{ errorMsg }}</span>
        <icon name="important" size="tiny" />
      </div>
    </div>
    <modal
      class="e-dropdown__options-modal"
      ref="optionsModal"
      @closed="onModalClosed"
      animation="bottom-slide-in"
      :fire-gtm="false"
      closable
    >
      <div class="e-dropdown__options-modal__title">
        <div class="e-dropdown__options-modal__label">{{ label }}</div>
      </div>
      <div class="e-dropdown__options-modal__search" v-if="showSearch">
        <div class="e-dropdown__options-modal__search-control">
          <icon name="search" />
          <input type="text" v-model="keyword" />
        </div>
      </div>
      <div class="e-dropdown__options-modal__content">
        <div
          class="e-dropdown__options-modal__item placeholder"
          @click="clearSelection"
          v-if="clearable && !$isNoE(selectedOption?.code)"
        >
          <div class="e-dropdown__options-modal__option">
            {{ $ifEmpty(clearText, placeholder) }}
          </div>
        </div>
        <div
          class="e-dropdown__options-modal__item"
          :class="{ selected: selectedOptionIds.includes(option?.code) }"
          v-for="(option, index) in filteredOptions"
          :key="index"
          @click="(e) => onOptionClick(e, option)"
        >
          <slot name="option" v-if="$slots.option" :option="option" />
          <div class="e-dropdown__options-modal__option" v-else>{{ option.text }}</div>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import { computed, defineComponent, nextTick, reactive, toRefs, watch } from 'vue';
import useDevice from '@/hooks/useDevice';
import { isNoW } from '@/utils/obj-utils';

export default defineComponent({
  name: 'Dropdown',
  emits: ['blur', 'change'],
  props: {
    title: {
      type: String,
    },
    label: {
      type: String,
    },
    placeholder: {
      type: String,
    },
    description: {
      type: String,
    },
    options: {
      type: Array,
    },
    selectedOption: {
      type: Array,
    },
    errorMsg: {
      type: String,
    },
    required: {
      type: Boolean,
      default: false,
    },
    clearable: {
      type: Boolean,
      default: false,
    },
    clearText: {
      type: String,
    },
    hideSelected: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    theme: {
      type: String,
      default: null,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, ctx) {
    let blurTimeout = null;
    const { deviceState } = useDevice();
    const state = reactive({
      rootEl: null,
      keyword: null,
      active: false,
      contentEl: null,
      optionsModal: null,
      searchEl: null,
    });
    const computes = {
      filteredOptions: computed(() => {
        let opts = props.options;
        if (props.hideSelected) {
          opts = opts.filter((x) => {
            props.selectedOption.forEach((item) => {
              x.code !== item?.code;
            });
          });
        }
        if (!isNoW(state.keyword)) {
          opts = opts.filter((x) => new RegExp(`^${state.keyword}`, 'gi').test(x.text));
        }
        return opts;
      }),
      showSearch: computed(() => props.options?.length > 10),
      selectedOptionText: computed(() => {
        return props.selectedOption
          .map((item) => {
            return item.text;
          })
          .join(',');
      }),
      selectedOptionIds: computed(() => {
        return props.selectedOption.map((item) => {
          return item.key;
        });
      }),
    };
    watch(
      () => state.active,
      () => {
        methods.checkMobileMotion();
      }
    );
    watch(
      () => deviceState.deviceType,
      () => {
        methods.checkMobileMotion();
      }
    );
    const methods = {
      onSearchBlur() {
        state.active = false;
      },
      blur(e) {
        if (blurTimeout) {
          clearTimeout(blurTimeout);
        }
        blurTimeout = setTimeout(() => {
          if (document.activeElement === state.searchEl) return;
          if (deviceState.deviceType.includes('desktop')) {
            state.active = false;
            ctx.emit('blur');
          }
        }, 50);
      },
      toggle() {
        if (props.disabled) return;
        state.active = !state.active;
      },
      clear(e) {
        ctx.emit('change', e, null);
      },
      async checkMobileMotion() {
        if (!deviceState.deviceType.includes('desktop')) {
          if (state.active) {
            state.optionsModal.open();
            await nextTick();
          } else {
            state.optionsModal.close();
          }
        } else {
          if (state.optionsModal) {
            state.optionsModal.close();
          }
        }
      },
      onOptionClick(e, option) {
        ctx.emit('change', e, option);
        state.active = false;
      },
      clearSelection(e) {
        ctx.emit('change', e, null);
        state.active = false;
      },
      onModalClosed() {
        state.active = false;
      },
    };
    return {
      ...toRefs(state),
      ...toRefs(deviceState),
      ...computes,
      ...methods,
    };
  },
});
</script>

<style lang="scss">
@import '../styles/variables';
@import '../styles/functions';
@import '../styles/mixins';
</style>
